/**
 * Check-in — domain-root derivations off `@momo/views/auth`'s shared
 * identity state. `canScanDoor` is the one condition every scan-permission-
 * gated Firestore listener in the scanner package enforces (door-labels.sync.ts,
 * tickets.sync.ts) — it mirrors Ticket's own `list` rule
 * (`tickets.checkin.scan` + mfa), so an anonymous or under-permissioned
 * session never opens a listener the rules deny. `hasScanPermission` is the
 * same predicate read reactively for render — mirrors web's own
 * admin.state.ts's `canManagePaymentSettings` shape: a permission-derived
 * computed sitting beside the imported session it reads, not folded into a
 * reconciler.
 *
 * `canUndoAdmission` mirrors Ticket's `unredeem` rule
 * (`tickets.checkin.undo` + mfa) — the session-level half of that rule; the
 * doc-level half is the ticket's own `status: redeemed` pre-state, which the
 * verdict on screen already answers.
 *
 * `canReadReports` mirrors `Order`'s own `list` rule (`tickets.reports.view`
 * + mfa) — the gate `manual-lookup.sync.ts` enforces before opening its own
 * local order index, so the dead-phone screen's search box (and the read it
 * requires) opens only for the actor the rules would actually let list
 * `orders`. An ordinary `tickets.checkin.scan`-only door reaches the same
 * screen but never this predicate's `true` branch — the escalate copy is
 * the honest answer for that account, not a placeholder.
 *
 * Lives at the domain root, not renamed from a "session" identity — these
 * are pure derivations off the one shared `session` value, not a second
 * identity source beside `@momo/views/auth`'s own singleton.
 * `packages/views/src/events/console/order-ops.state.ts` gates the same
 * `tickets.checkin.scan`/`tickets.checkin.undo` strings for the Orders-tab
 * buttons with a genuinely different condition — no `status`/`mfa` check —
 * so the two declarations stand separately rather than collapsing to one.
 */

import { expandPermission } from "@mhmo91/ican";
import { computed } from "@mhmo91/schmancy/state";
import { type SessionState, session } from "@momo/views/auth";
import { activeActor } from "../auth/actor.state.ts";
import { organizationSelection } from "../workspace/organization.state.ts";

export function canScanDoor(s: SessionState): boolean {
	return (
		s.status === "member" &&
		s.actor?.mfa === true &&
		[
			...(s.actor?.permissions ?? []),
			...(s.actor?.eventId ? (s.actor?.eventPermissions ?? []) : []),
		].some((permission) =>
			expandPermission("tickets.checkin.scan").includes(permission),
		)
	);
}

// Each gate below also requires the actor to be bound to the organization this
// document is open on, so a switch reads closed until the new role answers.
export const hasScanPermission = computed(
	() => activeActor.get() !== null && canScanDoor(session.value),
);

export function canUndoAdmission(s: SessionState): boolean {
	return (
		s.status === "member" &&
		s.actor?.mfa === true &&
		[
			...(s.actor?.permissions ?? []),
			...(s.actor?.eventId ? (s.actor?.eventPermissions ?? []) : []),
		].some((permission) =>
			expandPermission("tickets.checkin.undo").includes(permission),
		)
	);
}

export const hasUndoPermission = computed(
	() => activeActor.get() !== null && canUndoAdmission(session.value),
);

export function canReadReports(s: SessionState): boolean {
	return (
		s.status === "member" &&
		s.actor?.mfa === true &&
		[
			...(s.actor?.permissions ?? []),
			...(s.actor?.eventId ? (s.actor?.eventPermissions ?? []) : []),
		].some((permission) =>
			expandPermission("tickets.reports.view").includes(permission),
		)
	);
}

export const hasReportsPermission = computed(
	() => activeActor.get() !== null && canReadReports(session.value),
);

// Choosing a public event does not grant permission to scan its tickets.
export const canChooseScannerEvent = computed(
	() =>
		session.value.status === "member" &&
		activeActor.get()?.mfa === true &&
		(hasScanPermission.get() ||
			(activeActor
				.get()
				?.memberships?.includes(
					organizationSelection.get().organizationId ?? "",
				) === true &&
				(organizationSelection.get().eventIds?.length ?? 0) > 0)),
);
